/**
 * SeatManagementService
 * 좌석 관리 비즈니스 로직
 */
import type {
  ManagedSeat,
  SeatStatus,
  SeatActionType,
  SeatFilter,
  SeatSortType,
} from '../models/SeatManagement';

/**
 * SeatManagementService 클래스
 * 좌석 관리 관련 비즈니스 로직 및 유틸리티 함수
 */
export class SeatManagementService {
  /**
   * 사용 시간 계산 (분 → 시간:분)
   */
  getUsageTime(startTime?: string): string {
    if (!startTime) return '0시간 0분';

    const start = new Date(startTime);
    const now = new Date();
    const diffMs = now.getTime() - start.getTime();
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffMinutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));

    return `${diffHours}시간 ${diffMinutes}분`;
  }

  /**
   * 좌석 상태별 색상 클래스 반환
   */
  getStatusColor(status: SeatStatus): string {
    switch (status) {
      case 'available':
        return 'bg-green-100 border-green-300 text-green-800';
      case 'occupied':
        return 'bg-blue-100 border-blue-300 text-blue-800';
      case 'maintenance':
        return 'bg-yellow-100 border-yellow-300 text-yellow-800';
      case 'disabled':
        return 'bg-red-100 border-red-300 text-red-800';
      default:
        return 'bg-gray-100 border-gray-300 text-gray-800';
    }
  }

  /**
   * 좌석 상태별 텍스트 반환
   */
  getStatusText(status: SeatStatus): string {
    switch (status) {
      case 'available':
        return '사용 가능';
      case 'occupied':
        return '사용 중';
      case 'maintenance':
        return '점검 중';
      case 'disabled':
        return '사용 불가';
      default:
        return '알 수 없음';
    }
  }

  /**
   * 연결 상태별 배지 클래스 반환
   */
  getConnectionBadgeClass(connectionStatus: string): string {
    switch (connectionStatus) {
      case 'connected':
        return 'bg-green-100 text-green-800';
      case 'connecting':
        return 'bg-yellow-100 text-yellow-800';
      case 'disconnected':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  }

  /**
   * 연결 상태별 점 색상 클래스 반환
   */
  getConnectionDotClass(connectionStatus: string): string {
    switch (connectionStatus) {
      case 'connected':
        return 'bg-green-500';
      case 'connecting':
        return 'bg-yellow-500';
      case 'disconnected':
        return 'bg-red-500';
      default:
        return 'bg-gray-500';
    }
  }

  /**
   * 연결 상태별 텍스트 반환
   */
  getConnectionStatusText(connectionStatus: string): string {
    switch (connectionStatus) {
      case 'connected':
        return '실시간 연결';
      case 'connecting':
        return '연결 중';
      case 'disconnected':
        return '연결 끊김';
      default:
        return '알 수 없음';
    }
  }

  /**
   * 좌석 상태 변경에 필요한 액션 타입 결정
   */
  determineActionType(
    currentStatus: SeatStatus,
    newStatus: SeatStatus
  ): SeatActionType {
    if (newStatus === 'available') {
      // 현재 상태에 따라 액션 결정
      if (currentStatus === 'occupied') {
        return 'force_checkout'; // 사용중인 좌석을 사용가능으로
      } else {
        return 'toggle_maintenance'; // 점검모드 해제
      }
    } else if (newStatus === 'maintenance') {
      return 'toggle_maintenance';
    } else {
      // 사용중으로 변경하는 경우는 할당
      return 'assign_seat';
    }
  }

  /**
   * 좌석 필터링
   */
  filterSeats(seats: ManagedSeat[], filter: SeatFilter): ManagedSeat[] {
    if (filter === 'all') return seats;
    return seats.filter((seat) => seat.status === filter);
  }

  /**
   * 좌석 정렬
   */
  sortSeats(seats: ManagedSeat[], sortType: SeatSortType): ManagedSeat[] {
    return [...seats].sort((a, b) => {
      switch (sortType) {
        case 'number':
          return parseInt(a.number) - parseInt(b.number);
        case 'status':
          return a.status.localeCompare(b.status);
        case 'zone':
          return a.zone.localeCompare(b.zone);
        case 'usage_time':
          if (!a.start_time && !b.start_time) return 0;
          if (!a.start_time) return 1;
          if (!b.start_time) return -1;
          return new Date(b.start_time).getTime() - new Date(a.start_time).getTime();
        default:
          return 0;
      }
    });
  }

  /**
   * 날짜 포맷팅 — null/undefined/빈문자열/잘못된 값이면 '-' 반환
   * (방치하면 `new Date(null)` → 1970-01-01 오전 9:00:00 으로 표시되는 사고 발생)
   */
  formatDateTime(dateString: string | null | undefined): string {
    if (!dateString) return '-';
    try {
      const d = new Date(dateString);
      if (isNaN(d.getTime())) return '-';
      return d.toLocaleString('ko-KR');
    } catch {
      return '-';
    }
  }

  /**
   * 통계 카드 아이콘 색상 클래스 반환
   */
  getStatIconColorClass(type: 'total' | 'available' | 'occupied' | 'maintenance' | 'occupancy'): {
    background: string;
    icon: string;
  } {
    switch (type) {
      case 'total':
        return { background: 'bg-gray-100', icon: 'text-gray-600' };
      case 'available':
        return { background: 'bg-green-100', icon: 'text-green-600' };
      case 'occupied':
        return { background: 'bg-blue-100', icon: 'text-blue-600' };
      case 'maintenance':
        return { background: 'bg-yellow-100', icon: 'text-yellow-600' };
      case 'occupancy':
        return { background: 'bg-purple-100', icon: 'text-purple-600' };
      default:
        return { background: 'bg-gray-100', icon: 'text-gray-600' };
    }
  }

  /**
   * 통계 값 색상 클래스 반환
   */
  getStatValueColorClass(type: 'total' | 'available' | 'occupied' | 'maintenance' | 'occupancy'): string {
    switch (type) {
      case 'total':
        return 'text-gray-900';
      case 'available':
        return 'text-green-600';
      case 'occupied':
        return 'text-blue-600';
      case 'maintenance':
        return 'text-yellow-600';
      case 'occupancy':
        return 'text-purple-600';
      default:
        return 'text-gray-900';
    }
  }
}

/**
 * SeatManagementService 싱글톤 인스턴스
 */
export const seatManagementService = new SeatManagementService();
